// Babylon meshes for a Track: one mesh per material (road, lines, kerbs,
// run-off asphalt, grass, gravel, barriers) plus a large ground plane, all
// static: world matrices and materials are frozen after creation.

import { Color3, DynamicTexture, Mesh, Scene, StandardMaterial, VertexData } from './babylon';
import { asphaltTexture, barrierTexture, grassTexture, gravelTexture } from './textures';
import type { Track } from '../track/track';
import { buildTrackGeometry, type MeshData, type TrackGeometry } from '../track/geometry';

interface GroupStyle {
  diffuse: Color3;
  specular?: Color3;
  specularPower?: number;
  /** Polygon offset; more negative draws on top of coplanar layers below it. */
  zOffset: number;
  vertexColors?: boolean;
  twoSided?: boolean;
  /** Procedural texture (tinted by `diffuse`). */
  texture?: (scene: Scene) => DynamicTexture;
}

const STYLES: Record<Exclude<keyof TrackGeometry, 'bounds'>, GroupStyle> = {
  grass: { diffuse: new Color3(0.74, 0.82, 0.66), zOffset: -1, texture: grassTexture },
  gravel: { diffuse: new Color3(1, 1, 1), zOffset: -2, texture: gravelTexture },
  runoffAsphalt: { diffuse: new Color3(0.78, 0.78, 0.8), specular: new Color3(0.05, 0.05, 0.05), specularPower: 24, zOffset: -3, texture: asphaltTexture },
  road: { diffuse: new Color3(0.68, 0.68, 0.7), specular: new Color3(0.1, 0.1, 0.1), specularPower: 40, zOffset: -4, texture: asphaltTexture },
  kerbs: { diffuse: new Color3(1, 1, 1), specular: new Color3(0.1, 0.1, 0.1), specularPower: 32, zOffset: -5, vertexColors: true },
  lines: { diffuse: new Color3(0.92, 0.92, 0.9), zOffset: -6 },
  barriers: { diffuse: new Color3(1, 1, 1), specular: new Color3(0.1, 0.1, 0.1), specularPower: 16, zOffset: 0, twoSided: true, texture: barrierTexture },
};

/**
 * Lift vertices by the track elevation (no-op on flat tracks). Surfaces built
 * across the track (road, lines, run-off, grass, gravel) have UV v = distance
 * along the lap, so they take the exact height of their own cross-section and
 * stay stacked a few millimetres apart; kerbs and barriers use the nearest
 * track height.
 */
function applyElevation(track: Track, data: MeshData, alongInV: boolean): Float32Array {
  const positions = data.positions;
  if (!track.elevation) return positions;
  const out = new Float32Array(positions);
  let hint: number | undefined;
  for (let i = 0, v = 0; i < out.length; i += 3, v++) {
    if (alongInV) {
      out[i + 1] = out[i + 1]! + track.elevationAt(data.uvs[v * 2 + 1]!);
    } else {
      const h = track.heightNear(out[i]!, out[i + 2]!, hint);
      hint = h.index;
      out[i + 1] = out[i + 1]! + h.y;
    }
  }
  return out;
}

function makeMesh(scene: Scene, name: string, data: MeshData, style: GroupStyle, track: Track, alongInV: boolean): Mesh {
  const mesh = new Mesh(name, scene);
  const vd = new VertexData();
  vd.positions = applyElevation(track, data, alongInV);
  vd.normals = data.normals;
  vd.indices = data.indices;
  vd.uvs = data.uvs;
  if (data.colors) vd.colors = data.colors;
  vd.applyToMesh(mesh, false);

  const mat = new StandardMaterial(`${name}-mat`, scene);
  mat.diffuseColor = style.diffuse;
  if (style.texture) mat.diffuseTexture = style.texture(scene);
  mat.specularColor = style.specular ?? Color3.Black();
  if (style.specularPower) mat.specularPower = style.specularPower;
  mat.zOffset = style.zOffset;
  if (style.twoSided) {
    mat.backFaceCulling = false;
    mat.twoSidedLighting = true;
  }
  mesh.material = mat;
  mesh.useVertexColors = !!style.vertexColors;
  mesh.hasVertexAlpha = false;
  mesh.isPickable = false;
  mesh.doNotSyncBoundingInfo = true;
  mesh.freezeWorldMatrix();
  return mesh;
}

/**
 * Rubber and braking marks for the road, as a multiplier texture covering the
 * whole lap: u = lateral offset (−16..16 m), v = distance along the lap.
 * Darkest along the racing line, with smudges where cars brake hard.
 */
function rubberTexture(scene: Scene, track: Track, raceline: ReadonlyArray<{ x: number; z: number }>, brakingZones: ReadonlyArray<[number, number]>): DynamicTexture {
  const W = 64;
  // ~0.7 m per row: the line swings across the track before corners, and a
  // coarser texture turns that diagonal into blotches.
  const H = 8192;
  const L = track.length;
  // Racing line as (s, offset), sorted by s.
  const line = raceline
    .map((p) => {
      const pr = track.project(p.x, p.z);
      return { s: pr.s, offset: pr.offset };
    })
    .sort((a, b) => a.s - b.s);
  const offsetAt = (s: number): number => {
    let lo = 0;
    let hi = line.length - 1;
    while (hi - lo > 1) {
      const mid = (lo + hi) >> 1;
      if (line[mid]!.s <= s) lo = mid;
      else hi = mid;
    }
    const a = line[lo]!;
    const b = line[hi]!;
    const t = b.s > a.s ? Math.min(1, Math.max(0, (s - a.s) / (b.s - a.s))) : 0;
    return a.offset + (b.offset - a.offset) * t;
  };
  const inBraking = (s: number): number => {
    for (const [a, b] of brakingZones) {
      const d = (s - a + L) % L;
      const len = (b - a + L) % L;
      if (d <= len) return 0.5 + 0.5 * (d / len); // heavier towards the corner
    }
    return 0;
  };
  // No mipmaps: the texture is stretched ~2.7 m per texel along the lap, so at a
  // distance mip levels blur the thin rubber line sideways into dark bands across
  // the whole road. The content is smooth, so plain linear filtering doesn't shimmer.
  const tex = new DynamicTexture('tex-rubber', { width: W, height: H }, scene, false);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  const img = ctx.createImageData(W, H);
  for (let row = 0; row < H; row++) {
    // Canvas row 0 is the top of the image, which maps to v = 1.
    const s = ((H - 1 - row + 0.5) / H) * L;
    const centre = offsetAt(s);
    const brake = inBraking(s);
    for (let col = 0; col < W; col++) {
      const offset = (col + 0.5) / W * 32 - 16;
      const d = (offset - centre) / 1.4;
      // Rubbered line ~2.5 m wide, plus a fainter wider band where cars run.
      let k = 1 - 0.2 * Math.exp(-d * d) - 0.06 * Math.exp(-(d * d) / 9);
      k -= brake * 0.1 * Math.exp(-(d * d) / 2.2);
      const v = Math.max(0, Math.min(255, Math.round(k * 255)));
      const i = (row * W + col) * 4;
      img.data[i] = v;
      img.data[i + 1] = v;
      img.data[i + 2] = v;
      img.data[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  tex.update(true);
  tex.wrapU = 0; // clamp across the track
  tex.wrapV = 1;
  tex.uScale = 1 / 32;
  tex.uOffset = 0.5;
  tex.vScale = 1 / L;
  tex.coordinatesIndex = 0;
  return tex;
}

/** Large-scale light/dark variation for the grass, so it doesn't read as one flat carpet. */
function grassVariationTexture(scene: Scene): DynamicTexture {
  const S = 256;
  const tex = new DynamicTexture('tex-grass-var', { width: S, height: S }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.fillStyle = 'rgb(235,235,235)';
  ctx.fillRect(0, 0, S, S);
  let seed = 7;
  const rand = (): number => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  for (let i = 0; i < 140; i++) {
    const x = rand() * S;
    const y = rand() * S;
    const r = 10 + rand() * 40;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    const dark = rand() < 0.6;
    g.addColorStop(0, dark ? 'rgba(150,150,150,0.5)' : 'rgba(255,255,255,0.5)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  tex.update(true);
  tex.wrapU = 1;
  tex.wrapV = 1;
  // Grass UVs are metres; one tile every 180 m.
  tex.uScale = 1 / 180;
  tex.vScale = 1 / 180;
  tex.coordinatesIndex = 0;
  return tex;
}

export interface TrackMeshOptions {
  /** Racing line (world x/z) for rubber marks on the road. */
  raceline?: ReadonlyArray<{ x: number; z: number }>;
  /** [startS, endS] braking zones for darker marks before big stops. */
  brakingZones?: ReadonlyArray<[number, number]>;
  /**
   * Freeze the materials once the scene is first ready (default true). Frozen
   * materials don't pick up lights or shadows added later; call
   * `material.unfreeze()` on the returned meshes' materials if you add some.
   */
  freezeMaterials?: boolean;
}

/**
 * Build the static track scenery: a ground plane plus one mesh per material
 * (8 draw calls). Call once per track; dispose the returned meshes together.
 */
export function buildTrackMeshes(scene: Scene, track: Track, options: TrackMeshOptions = {}): { meshes: Mesh[] } {
  const geo = buildTrackGeometry(track);
  const meshes: Mesh[] = [];

  for (const key of Object.keys(STYLES) as (keyof typeof STYLES)[]) {
    const data = geo[key];
    if (data.indices.length === 0) continue;
    const mesh = makeMesh(scene, `track-${key}`, data, STYLES[key], track, key !== 'kerbs' && key !== 'barriers');
    const mat = mesh.material as StandardMaterial;
    // Multiplier overlays: rubber on the road, broad variation on the grass.
    if (key === 'road' && options.raceline) {
      mat.lightmapTexture = rubberTexture(scene, track, options.raceline, options.brakingZones ?? []);
      mat.useLightmapAsShadowmap = true;
    } else if (key === 'grass') {
      mat.lightmapTexture = grassVariationTexture(scene);
      mat.useLightmapAsShadowmap = true;
    }
    meshes.push(mesh);
  }
  if (options.freezeMaterials !== false) {
    // Freeze after the first compile so the effects include the scene's lights.
    scene.executeWhenReady(() => {
      for (const m of meshes) if (!m.isDisposed()) m.material?.freeze();
    });
  }
  return { meshes };
}
